import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  minHeight?: number;
}

const ExpandableText = ({ text, maxLines = 3, className, minHeight }: ExpandableTextProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const isOverflowing = contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setShowButton(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text]);

  const lineClampStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxLines,
    overflow: 'hidden',
    minHeight: minHeight ? `${minHeight}px` : 'auto',
    transition: 'all 0.3s ease-in-out',
  };

  return (
    <div className="flex flex-col gap-2 items-start w-full">
      <div className="relative w-full">
        <p
          ref={contentRef}
          className={className}
          style={!isExpanded ? lineClampStyle : { minHeight: minHeight ? `${minHeight}px` : 'auto' }}
        >
          {text}
        </p>
        {!isExpanded && showButton && (
          <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-white/50 to-transparent pointer-events-none" />
        )}
      </div>
      {showButton && (
        <Button variant="link" onClick={() => setIsExpanded(!isExpanded)} className="p-0 h-auto text-sm font-semibold text-slate-300">
          {isExpanded ? t('common.less') : t('common.more')}
        </Button>
      )}
    </div>
  );
};

export default ExpandableText;
